'use client'
import React, { useState, useMemo, useCallback, useEffect, Suspense } from 'react'
import {
  useDefaultsFromURLSearch,
  useDerivedSwapInfo,
  useSwapActionHandlers,
  useSwapState
} from '@/state/swap/hooks'
import { motion, AnimatePresence } from 'framer-motion'
import useGasPrice from '@/hooks/useGasPrice'
import { useClientSession } from '@/discord/hooks/useFetchSession'
import { useSwapCallback } from '@/hooks/useSwapCallback'
import { useAppDispatch } from '@/state/store'
import { useUserSlippageTolerance } from '@/state/user/hooks'
import { Field, setEnterRaffle, SwapDelay } from '@/state/swap/actions'
import { AdvancedSwapDetails } from './AdvancedSwapDetails'
import CurrencyInput from '@/components/CurrencyInput/CurrencyInput'
import ConfirmSwapModal from './ConfirmSwapModal'
import { AddressInput } from '@/components'
import 'react-loading-skeleton/dist/skeleton.css'
import { ALLOWED_PRICE_IMPACT_HIGH } from '@/constants'
import WarningSlippageComponent from '../common/WarningSlippage'
import { releasePoints } from '@/discord/api'
import { sendGAEvent } from '@next/third-parties/google'
import toast from 'react-hot-toast'
import {
  useWalletData,
  useIsSupportedNetwork,
  confirmPriceImpactWithoutFee,
  maxAmountSpend,
  halfAmountSpend,
  isBackpackProvider
} from '@/utils'
import {
  ApprovalState,
  useApproveCallbackFromTrade
} from '@/hooks/useApproveCallback'

import {
  CurrencyAmount,
  JSBI,
  Trade,
  WMND,
  Token,
  MON,
  currencyEquals,
  NativeCurrency
} from '@monadex/sdk'
import { Box, CircularProgress } from '@mui/material'
import { Button } from '@mui/base'
import { useTransactionFinalizer } from '@/state/transactions/hooks'
import useWrapCallback, { WrapType } from '@/hooks/useWrapCallback'
import { computeTradePriceBreakdown, warningSeverity } from '@/utils/price'
import { useAllTokens, _useCurrency } from '@/hooks/Tokens'
import { wrappedCurrency } from '@/utils/wrappedCurrency'
import { SLIPPAGE_AUTO } from '@/state/user/reducer'
import useParsedQueryString from '@/hooks/useParseQueryString'
import { usePathname } from 'next/navigation'
import useSwapRedirects from '@/hooks/useSwapRedirect'
import { updateUserBalance } from '@/state/balance/action'
import { UnfoldLessIcon, UnfoldMoreIcon, Exchange01Icon } from '@/utils/icons'
import SwapButton from './SwapButton'
import { RaffleWrapper } from './RaffleWrapper'
import { useAppKitProvider } from '@reown/appkit/react'
import { isTokenInRaffleWhitelist } from '@/utils/raffleTokens'

enum MethodType {
  SWAP,
  ADD_LIQUIDITY
}

export const getRaffleTokenField = (currencies: {
  INPUT?: NativeCurrency | Token | undefined
  OUTPUT?: NativeCurrency | Token | undefined
}): Field | undefined => {
  if (currencies.INPUT instanceof Token && isTokenInRaffleWhitelist(currencies.INPUT.address)) {
    return Field.INPUT
  }
  if (currencies.OUTPUT instanceof Token && isTokenInRaffleWhitelist(currencies.OUTPUT.address)) {
    return Field.OUTPUT
  }
  return undefined
}

const Swap: React.FC = () => {
  const [mounted, setMounted] = useState(false)
  const loadedUrlParams = useDefaultsFromURLSearch()
  const pathname = usePathname()
  const isSupportedNetwork = useIsSupportedNetwork()
  const { data: session, isAuthenticated } = useClientSession()
  const [dismissTokenWarning, setDismissTokenWarning] = useState<boolean>(false)
  const [dropdownDetails, setDropDownDetails] = useState<boolean>(false)
  // dismiss warning if all imported tokens are in active lists
  const defaultTokens = useAllTokens()

  const { account, chainId } = useWalletData()
  const dispatch = useAppDispatch()
  const { independentField, typedValue, recipient, swapDelay, fractionOfSwapAmount, enterRaffle } = useSwapState()
  const {
    v2Trade, // eeror potential here on input
    currencyBalances,
    parsedAmount,
    currencies,
    inputError: swapInputError,
    useAutoSlippage: autoSlippage
  } = useDerivedSwapInfo()
  const finalizedTransaction = useTransactionFinalizer()
  const {
    wrapType,
    execute: onWrap,
    inputError: wrapInputError
  } = useWrapCallback(
    currencies[Field.INPUT],
    currencies[Field.OUTPUT],
    typedValue
  )
  const showWrap: boolean = wrapType !== WrapType.NOT_APPLICABLE
  // const gasEstimation = useGasPrice()
  const trade = showWrap ? undefined : v2Trade
  const {
    onCurrencySelection,
    onUserInput,
    onRecipientChange
  } = useSwapActionHandlers()
  let [allowedSlippage] = useUserSlippageTolerance()
  allowedSlippage =
    allowedSlippage === SLIPPAGE_AUTO ? autoSlippage : allowedSlippage
  const [approving, setApproving] = useState(false)

  const [approval, approveCallback, raffleApprovalState, inputApprovalState] = useApproveCallbackFromTrade(
    trade,
    allowedSlippage,
    getRaffleTokenField(currencies)
  )


  const { walletProvider } = useAppKitProvider('eip155')
  // check if backpack wallet is being used
  const isBackpack = isBackpackProvider(walletProvider) ? walletProvider.isBackpack : false
  const dependentField: Field =
    independentField === Field.INPUT ? Field.OUTPUT : Field.INPUT
  const parsedAmounts = useMemo(() => {
    return showWrap
      ? {
          [Field.INPUT]: parsedAmount,
          [Field.OUTPUT]: parsedAmount
        }
      : {
          [Field.INPUT]:
          independentField === Field.INPUT
            ? parsedAmount
            : trade?.inputAmount,
          [Field.OUTPUT]:
          independentField === Field.OUTPUT
            ? parsedAmount
            : trade?.outputAmount
        }
  }, [parsedAmount, independentField, trade, showWrap])

  const formattedAmounts = useMemo(() => {
    return {
      [independentField]: typedValue,
      [dependentField]: showWrap
        ? parsedAmounts[independentField]?.toExact() ?? ''
        : parsedAmounts[dependentField]?.toExact() ?? ''
    }
  }, [independentField, typedValue, dependentField, showWrap, parsedAmounts])
  const route = trade?.route
  const userHasSpecifiedInputOutput = Boolean(
    currencies[Field.INPUT] != null &&
    currencies[Field.OUTPUT] != null &&
    parsedAmounts[independentField]?.greaterThan(JSBI.BigInt(0))
  )
  const noRoute = route == null
  const { priceImpactWithoutFee } = computeTradePriceBreakdown(trade)
  const [approvalSubmitted, setApprovalSubmitted] = useState<boolean>(false)
  const [mainPrice, setMainPrice] = useState(true)
  const priceImpactSeverity = warningSeverity(priceImpactWithoutFee)
  const raffleField = getRaffleTokenField(currencies) as Field

  const isValid = !swapInputError
  const showApproveFlow =
    !swapInputError &&
    (approval === ApprovalState.NOT_APPROVED ||
      approval === ApprovalState.PENDING ||
      (approvalSubmitted && approval === ApprovalState.APPROVED)) &&
    !(priceImpactSeverity > 3)
  useEffect(() => {
    if (approval === ApprovalState.PENDING) {
      setApprovalSubmitted(true)
    } else if (approval === ApprovalState.APPROVED) {
      setApprovalSubmitted(false)
    }
  }, [approval, approvalSubmitted])

  const parsedQs = useParsedQueryString()
  const { redirectWithCurrency, redirectWithSwitch } = useSwapRedirects()
  const parsedCurrency0Id = (parsedQs.currency0 ??
    parsedQs.inputCurrency) as string
  const parsedCurrency1Id = (parsedQs.currency1 ??
    parsedQs.outputCurrency) as string

  const handleCurrencySelect = useCallback(
    (inputCurrency: Token | NativeCurrency) => {
      setApprovalSubmitted(false) // reset 2 step UI for approvals
      const isSwichRedirect = currencyEquals(inputCurrency, MON)
        ? parsedCurrency1Id === 'MON'
        : Boolean(parsedCurrency1Id) &&
        inputCurrency !== undefined &&
        Boolean(inputCurrency instanceof Token && inputCurrency.address) &&
        inputCurrency instanceof Token &&
        inputCurrency.address.toLowerCase() ===
        parsedCurrency1Id.toLowerCase()
      if (isSwichRedirect) {
        redirectWithSwitch()
      } else {
        if (
          !(
            inputCurrency instanceof Token &&
            inputCurrency.address in defaultTokens
          )
        ) {
          setDismissTokenWarning(false)
        }
        redirectWithCurrency(inputCurrency, true)
      }
    },
    [parsedCurrency1Id, redirectWithCurrency, redirectWithSwitch, defaultTokens]
  )

  const handleOtherCurrencySelect = useCallback(
    (outputCurrency: Token | NativeCurrency) => {
      const isSwichRedirect = currencyEquals(outputCurrency, MON)
        ? parsedCurrency0Id === 'MON'
        : Boolean(parsedCurrency0Id) &&
        outputCurrency &&
        Boolean(outputCurrency instanceof Token && outputCurrency.address) &&
        outputCurrency instanceof Token &&
        outputCurrency.address.toLowerCase() ===
        parsedCurrency0Id.toLowerCase()
      if (isSwichRedirect) {
        redirectWithSwitch()
      } else {
        if (
          !(
            outputCurrency instanceof Token &&
            outputCurrency.address in defaultTokens
          )
        ) {
          setDismissTokenWarning(false)
        }
        redirectWithCurrency(outputCurrency, false)
      }
    },
    [parsedCurrency0Id, redirectWithCurrency, redirectWithSwitch, defaultTokens]
  )
  const parsedCurrency0 = _useCurrency(parsedCurrency0Id)
  useEffect(() => {
    if (parsedCurrency0 != null) {
      onCurrencySelection(Field.INPUT, parsedCurrency0)
    } else if (
      pathname !== '/' &&
      parsedCurrency0 === undefined &&
      !parsedCurrency1Id
    ) {
      redirectWithCurrency(MON, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedCurrency0, parsedCurrency1Id])

  const parsedCurrency1 = _useCurrency(parsedCurrency1Id)
  useEffect(() => {
    if (parsedCurrency1 != null) {
      onCurrencySelection(Field.OUTPUT, parsedCurrency1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedCurrency1Id])

  const selectedTokens: Token[] = useMemo(
    () =>
      [parsedCurrency0, parsedCurrency1]?.filter(
        (c): c is Token => c instanceof Token
      ) ?? [],
    [parsedCurrency0, parsedCurrency1]
  )
  // TODO: check if needed
  const selectedTokensNotInDefault = selectedTokens?.filter((token: Token) => {
    return !(token.address in defaultTokens)
  })
  const { callback: swapCallback, error: swapCallbackError } = useSwapCallback(
    trade,
    allowedSlippage,
    recipient,
    fractionOfSwapAmount,
    enterRaffle
  )
  const [
    {
      showConfirm,
      txPending,
      tradeToConfirm,
      swapErrorMessage,
      attemptingTxn,
      txHash
    },
    setSwapState
  ] = useState<{
    showConfirm: boolean
    txPending?: boolean
    tradeToConfirm: Trade | undefined
    attemptingTxn: boolean
    swapErrorMessage: string | undefined
    txHash: string | undefined
  }>({
    showConfirm: false,
    txPending: false,
    tradeToConfirm: undefined,
    attemptingTxn: false,
    swapErrorMessage: undefined,
    txHash: undefined
  })

  const handleTypeInput = useCallback(
    (value: string) => {
      onUserInput(Field.INPUT, value)
    },
    [onUserInput]
  )
  const handleTypeOutput = useCallback(
    (value: string) => {
      onUserInput(Field.OUTPUT, value)
    },
    [onUserInput]
  )

  const maxAmountInput: CurrencyAmount | undefined = maxAmountSpend(
    chainId,
    currencyBalances[Field.INPUT]
  )

  const halfAmountInput: CurrencyAmount | undefined = halfAmountSpend(
    chainId,
    currencyBalances[Field.INPUT]
  )

  const handleMaxInput = useCallback(() => {
    maxAmountInput != null && onUserInput(Field.INPUT, maxAmountInput.toExact())
  }, [maxAmountInput, onUserInput])

  const handleHalfInput = useCallback(() => {
    if (halfAmountInput == null) {
      return
    }

    onUserInput(Field.INPUT, halfAmountInput.toExact())
  }, [halfAmountInput, onUserInput])

  const atMaxAmountInput = Boolean(
    maxAmountInput != null &&
    parsedAmounts[Field.INPUT]?.equalTo(maxAmountInput)
  )

  const onSwap = (): void => {
    if (showWrap && onWrap != null) {
      void onWrap()
    } else {
      setSwapState({
        tradeToConfirm: trade,
        attemptingTxn: false,
        swapErrorMessage: undefined,
        showConfirm: true,
        txHash: undefined
      })
    }
  }

  const handleAcceptChanges = useCallback(() => {
    setSwapState({
      tradeToConfirm: trade,
      swapErrorMessage,
      txHash,
      attemptingTxn,
      showConfirm
    })
  }, [attemptingTxn, showConfirm, swapErrorMessage, trade, txHash])

  const handleConfirmDismiss = useCallback(() => {
    setSwapState({
      showConfirm: false,
      tradeToConfirm,
      attemptingTxn,
      swapErrorMessage,
      txHash
    })

    if (txHash) {
      onUserInput(Field.INPUT, '')
    }
  }, [attemptingTxn, onUserInput, swapErrorMessage, tradeToConfirm, txHash])
  const fromTokenWrapped = wrappedCurrency(currencies[Field.INPUT], chainId)

  const handleSwap = useCallback(() => {
    if (
      priceImpactWithoutFee != null &&
      !confirmPriceImpactWithoutFee(priceImpactWithoutFee)
    ) {
      return
    }
    if (swapCallback == null) {
      return
    }
    setSwapState({
      attemptingTxn: true,
      tradeToConfirm,
      showConfirm,
      swapErrorMessage: undefined,
      txHash: undefined
    })
    swapCallback()
      .then(async ({ response, summary }) => {
        setSwapState({
          attemptingTxn: false,
          txPending: true,
          tradeToConfirm,
          showConfirm,
          swapErrorMessage: undefined,
          txHash: response.hash
        })

        try {
          const receipt = await response.wait()
          finalizedTransaction(receipt, {
            summary
          })

          dispatch(updateUserBalance())

          setSwapState({
            attemptingTxn: false,
            txPending: false,
            tradeToConfirm,
            showConfirm,
            swapErrorMessage: undefined,
            txHash: response.hash
          })
          // dispatch enter raffles to false after swap to keep the approval flow correct
          if (enterRaffle) {
            // If enterRaffle is true, toggle it to false
            dispatch(setEnterRaffle())
          }
          sendGAEvent(
            'event',
            'swap',
            {
              label: recipient === null
                ? 'swap w/o Send'
                : recipient === account
                  ? 'swap w/o Send + recipient'
                  : 'swap w/ Send',
              value: [
                trade?.inputAmount?.currency?.symbol,
                isBackpack && 'Backpack used',
                trade?.outputAmount?.currency?.symbol
              ].join('/')
            })
          if ((session?.user) != null) {
            const points = await releasePoints(
              session.user.id,
              'SWAP',
              response.hash,
              isBackpack,
              '0x',
              account
            )
            console.log('points', points)
            if (points != null) {
              toast(`Successfully credited ${points.pointsAwarded} BXP for your transaction. You now have ${points.totalPoints} points. Points may take a few minutes to display, please be patient.`, {
                duration: 4000
              })
            }
          }
        } catch (error: any) {
          setSwapState({
            attemptingTxn: false,
            tradeToConfirm,
            showConfirm,
            swapErrorMessage: error?.message,
            txHash: undefined
          })
        }
      })
      .catch(error => {
        setSwapState({
          attemptingTxn: false,
          tradeToConfirm,
          showConfirm,
          swapErrorMessage: error?.message,
          txHash: undefined
        })
      })
  }, [
    // onV2TradeAnalytics,
    trade,
    priceImpactWithoutFee,
    swapCallback,
    tradeToConfirm,
    showConfirm,
    finalizedTransaction,
    recipient,
    account,
    fromTokenWrapped,
    chainId,
    formattedAmounts
  ])
  const fetchingBestRoute =
    swapDelay === SwapDelay.USER_INPUT ||
    swapDelay === SwapDelay.FETCHING_SWAP ||
    swapDelay === SwapDelay.FETCHING_BONUS

  const handleApprove = async (): Promise<void> => {
    setApproving(true)
    try {
      await approveCallback()
      setApproving(false)
    } catch (err) {
      setApproving(false)
    }
  }
  /**
   @tip avoid Error: Text content does not match server-rendered HTML.
   @See more info here: https://nextjs.org/docs/messages/react-hydration-error
  */
  useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted) {
    return null
  }

  return (
    <Suspense>
      <Box className='relative'>
        {showConfirm && (
          <ConfirmSwapModal
            isOpen={showConfirm}
            trade={trade}
            originalTrade={tradeToConfirm}
            onAcceptChanges={handleAcceptChanges}
            attemptingTxn={attemptingTxn}
            txPending={txPending}
            txHash={txHash}
            recipient={recipient}
            allowedSlippage={allowedSlippage}
            onConfirm={handleSwap}
            swapErrorMessage={swapErrorMessage}
            onDismiss={handleConfirmDismiss}
            raffleFormattedAmount={raffleField ? formattedAmounts[raffleField] : undefined}
            raffleField={raffleField}
          />
        )}
        <CurrencyInput
          title='Pay'
          id='swap-currency-input'
          currency={currencies[Field.INPUT]}
          onHalf={handleHalfInput}
          onMax={handleMaxInput}
          showHalfButton
          showMaxButton={!atMaxAmountInput}
          otherCurrency={currencies[Field.OUTPUT]}
          handleCurrencySelect={handleCurrencySelect}
          amount={formattedAmounts[Field.INPUT]}
          setAmount={handleTypeInput}
          color='secondary'
        />
        <Box className='cursor-pointer flex justify-center items-center absolute border border-bgColor border-4 bg-primary/20 rounded-lg fixed top-0.6 left-1/2 transform -translate-x-1/2 -translate-y-1/2 p-1'>
          <Exchange01Icon
            onClick={redirectWithSwitch}
            className='text-primary animate-spin animate-once animate-ease-in'
          />
        </Box>
        <CurrencyInput
          title='Receive'
          data-cy='token-amount-output'
          id='swap-currency-output'
          currency={currencies[Field.OUTPUT]}
          showPrice={Boolean(trade?.executionPrice)}
          showMaxButton={false}
          otherCurrency={currencies[Field.INPUT]}
          handleCurrencySelect={handleOtherCurrencySelect}
          amount={formattedAmounts[Field.OUTPUT]}
          setAmount={handleTypeOutput}
          color='secondary'
        />
        <motion.div
          animate={{ opacity: 0.5 }}
          transition={{
            default: { ease: 'linear' },
            layout: { duration: 0.3 }
          }}
        >
          <WarningSlippageComponent trade={trade as Trade} />
        </motion.div>
        {!showWrap && (
          <Box className=''>
            <Box className='flex space-between items-center  px-2 py-1 gap-2'>
              <Button
                onClick={() => onRecipientChange(recipient !== null ? null : '')}
                className='text-sm font-clash text-white/60'
              >
                {recipient !== null
                  ? 'Remove Recipient -'
                  : 'Add Recipient +'}
              </Button>
            </Box>
            {recipient !== null && (
              <AddressInput
                label=''
                placeholder='Destination Address'
                value={recipient}
                onChange={onRecipientChange}
              />
            )}
          </Box>
        )}
        {trade?.executionPrice != null && (
          <>
            <Box
              className='flex gap-2 opacity-80 mt-2 font-regular border py-2 border-primary/30 rounded-md p-2 justify-between items-center ease-out mb-5'
              onClick={() => setDropDownDetails(!dropdownDetails)}
            >
              <p className='flex gap-2 items-center'>
                1{' '}
                {
              (mainPrice ? currencies[Field.INPUT] : currencies[Field.OUTPUT])
                ?.symbol
            }{' '}
                ≈ {' '}
                {(mainPrice
                  ? trade.executionPrice
                  : trade.executionPrice.invert()
                ).toSignificant(6)}{' '}
                {
              (mainPrice ? currencies[Field.OUTPUT] : currencies[Field.INPUT])
                ?.symbol
            }{' '}
                <Exchange01Icon
                  width={18}
                  className='text-sm cursor-pointer text-white'
                  onClick={() => {
                    setMainPrice(!mainPrice)
                  }}
                />
              </p>
              {dropdownDetails ? <UnfoldLessIcon className='text-white cursor-pointer' /> : <UnfoldMoreIcon className='text-white cursor-pointer' />}
            </Box>
            <RaffleWrapper amount={formattedAmounts[raffleField]} field={raffleField} />
          </>
        )}

        {dropdownDetails && (
          !showWrap && fetchingBestRoute
            ? (
              <Box mt={2} className='transition-all duration-300 ease-in-out text-center'>
                <CircularProgress className='text-semibold' size={16} />
                <p className='text-xs mb-2'>Fetching Best Quote</p>
              </Box>
              )
            : (
              <Box className='transition-all duration-300 ease-in-out'>
                <AdvancedSwapDetails trade={trade} />
              </Box>

              )
        )}
        <SwapButton
          id='swap-button'
          account={account as string}
          isSupportedNetwork={isSupportedNetwork}
          currencies={currencies}
          formattedAmounts={formattedAmounts}
          showWrap={showWrap}
          noRoute={noRoute}
          userHasSpecifiedInputOutput={userHasSpecifiedInputOutput}
          priceImpactSeverity={priceImpactSeverity}
          wrapInputError={wrapInputError}
          wrapType={wrapType}
          chainId={chainId}
          swapInputError={swapInputError}
          swapCallbackError={swapCallbackError}
          showApproveFlow={showApproveFlow}
          isValid={isValid}
          approval={approval}
          raffleApprovalState={raffleApprovalState}
          inputApprovalState={inputApprovalState}
          onSwap={onSwap}
          handleApprove={handleApprove}
        />
      </Box>
    </Suspense>
  )
}
export default Swap
